import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  format,
  parseISO,
  addDays,
  startOfWeek,
  isSameDay,
  isToday,
  isPast,
} from 'date-fns';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { adminBookingService } from '@/services/api/admin-booking.service';
import { useAuth } from '@/contexts/auth-context';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import type { Booking, AvailableSlot } from '@/types/booking';

interface RescheduleBookingDialogProps {
  booking: Booking;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string | null;
}

export function RescheduleBookingDialog({ booking, open, onOpenChange, bookingId }: RescheduleBookingDialogProps) {
  const { t } = useTranslation(['bookings', 'common']);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const dateLocale = getDateLocale();
  const dateFormats = getDateFormats();

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [datesWithSlots, setDatesWithSlots] = useState<Set<string>>(new Set());
  const [slotsData, setSlotsData] = useState<AvailableSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  const serviceId = booking.service?.id || booking.availableSlot?.service?.id;
  const providerId = booking.provider?.id || booking.availableSlot?.provider?.id;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedSlot(null);
      setSelectedDate(new Date());
      setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    }
  }, [open]);

  // Load available slots for selected date
  useEffect(() => {
    const loadSlots = async () => {
      if (!open || !serviceId) return;

      setIsLoadingSlots(true);
      try {
        const slots = await adminBookingService.getAvailabilitySlotsForDate({
          serviceId,
          date: format(selectedDate, 'yyyy-MM-dd'),
          providerId,
          organizationId: user?.organizationId,
          excludeBookingId: booking.id,
        });
        setSlotsData(slots);
      } catch (error) {
        console.error('Failed to load slots:', error);
        setSlotsData([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };

    loadSlots();
  }, [open, serviceId, providerId, selectedDate, user?.organizationId]);

  // Load available dates for the week
  useEffect(() => {
    const loadWeekDates = async () => {
      if (!open || !serviceId) return;

      try {
        const weekEnd = addDays(currentWeekStart, 6);
        const availableDates = await adminBookingService.getAvailableDatesInRange({
          serviceId,
          startDate: format(currentWeekStart, 'yyyy-MM-dd'),
          endDate: format(weekEnd, 'yyyy-MM-dd'),
          providerId,
          organizationId: user?.organizationId,
          excludeBookingId: booking.id,
        });
        setDatesWithSlots(availableDates);
      } catch (error) {
        console.error('Failed to load week dates:', error);
        setDatesWithSlots(new Set());
      }
    };

    loadWeekDates();
  }, [open, serviceId, providerId, currentWeekStart, user?.organizationId]);

  const rescheduleMutation = useMutation({
    mutationFn: () => {
      if (!selectedSlot) throw new Error('No slot selected');
      const startTime = selectedSlot.start_time || selectedSlot.startTime;
      if (!startTime) throw new Error('No start time');

      return adminBookingService.rescheduleBooking(
        booking.public_id || booking.publicId || booking.id,
        {
          start_time: startTime,
          provider_id: (selectedSlot as any).provider_id || providerId,
          service_id: serviceId,
        }
      );
    },
    onSuccess: () => {
      toast.success(t('bookings:toasts.rescheduleSuccess', 'Booking rescheduled successfully'));
      queryClient.invalidateQueries({ queryKey: ['admin-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message || 'Failed to reschedule booking';
      toast.error(message);
    },
  });

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeekStart =
      direction === 'prev'
        ? addDays(currentWeekStart, -7)
        : addDays(currentWeekStart, 7);
    setCurrentWeekStart(newWeekStart);
    setSelectedDate(newWeekStart);
    setSelectedSlot(null);
  };

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i)),
    [currentWeekStart]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('bookings:reschedule.title', 'Reschedule Booking')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current appointment info */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <p className="text-gray-600">
              {t('bookings:reschedule.currentTime', 'Current time')}:{' '}
              <span className="font-medium text-gray-900">
                {booking.start_time
                  ? format(parseISO(booking.start_time), `${dateFormats.fullWithWeekday} HH:mm`, { locale: dateLocale })
                  : booking.availableSlot?.startTime &&
                    format(parseISO(booking.availableSlot.startTime), `${dateFormats.fullWithWeekday} HH:mm`, { locale: dateLocale })}
              </span>
            </p>
          </div>

          {/* Week Calendar */}
          <div>
            <div className="flex flex-col gap-2 mb-3 md:flex-row md:items-center md:justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                {t('bookings:reschedule.selectNewTime', 'Select new date & time')}
              </h3>
              <div className="flex items-center justify-between md:justify-end gap-1 md:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-w-[44px] min-h-[44px] px-2 md:px-3"
                  onClick={() => navigateWeek('prev')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs md:text-sm font-medium text-gray-600 min-w-[140px] md:min-w-[200px] text-center">
                  {format(currentWeekStart, dateFormats.dayMonth, { locale: dateLocale })} - {format(addDays(currentWeekStart, 6), dateFormats.medium, { locale: dateLocale })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-w-[44px] min-h-[44px] px-2 md:px-3"
                  onClick={() => navigateWeek('next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Mobile: Horizontal scrollable day cards */}
            <div className="md:hidden overflow-x-auto -mx-6 px-6 snap-x snap-mandatory">
              <div className="flex gap-2 pb-2">
                {weekDays.map((day) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const hasSlots = datesWithSlots.has(dateStr);
                  const isSelected = isSameDay(day, selectedDate);
                  const isCurrentDay = isToday(day);
                  const dayIsPast = isPast(day) && !isCurrentDay;

                  return (
                    <button
                      key={dateStr}
                      type="button"
                      disabled={dayIsPast}
                      onClick={() => {
                        if (!dayIsPast) {
                          setSelectedDate(day);
                          setSelectedSlot(null);
                        }
                      }}
                      className={cn(
                        'relative min-w-[72px] h-[76px] flex-shrink-0 rounded-lg text-center snap-center',
                        isSelected
                          ? 'bg-blue-500 text-white shadow-md'
                          : dayIsPast
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : isCurrentDay
                          ? 'bg-blue-50 border-2 border-blue-300 active:bg-blue-100'
                          : 'bg-white border border-gray-200 active:border-blue-300 active:shadow-sm'
                      )}
                    >
                      {hasSlots && !dayIsPast && (
                        <div className="absolute top-2 right-2">
                          <div className={cn('w-3 h-3 rounded-full', isSelected ? 'bg-white' : 'bg-green-500')} />
                        </div>
                      )}
                      <div className="flex flex-col items-center justify-center h-full px-2">
                        <p className="text-xs font-medium mb-1">{format(day, 'EEE', { locale: dateLocale })}</p>
                        <p className="text-2xl font-bold leading-none">{format(day, 'd')}</p>
                        <p className="text-xs mt-1 opacity-75">{format(day, 'MMM', { locale: dateLocale })}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Desktop: 7-column grid */}
            <div className="hidden md:grid grid-cols-7 gap-2">
              {weekDays.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const hasSlots = datesWithSlots.has(dateStr);
                const isSelected = isSameDay(day, selectedDate);
                const isCurrentDay = isToday(day);
                const dayIsPast = isPast(day) && !isCurrentDay;

                return (
                  <button
                    key={dateStr}
                    type="button"
                    disabled={dayIsPast}
                    onClick={() => {
                      if (!dayIsPast) {
                        setSelectedDate(day);
                        setSelectedSlot(null);
                      }
                    }}
                    className={cn(
                      'relative min-h-[80px] p-4 rounded-lg text-center transition-all',
                      isSelected
                        ? 'bg-blue-500 text-white shadow-md'
                        : dayIsPast
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : isCurrentDay
                        ? 'bg-blue-50 border-2 border-blue-300 hover:bg-blue-100'
                        : 'bg-white border border-gray-200 hover:border-blue-300 hover:shadow-sm'
                    )}
                  >
                    {hasSlots && !dayIsPast && (
                      <div className="absolute top-2 right-2">
                        <div className={cn('w-3 h-3 rounded-full', isSelected ? 'bg-white' : 'bg-green-500')} />
                      </div>
                    )}
                    <p className="text-xs font-medium mb-1">{format(day, 'EEE', { locale: dateLocale })}</p>
                    <p className="text-2xl font-bold">{format(day, 'd')}</p>
                    <p className="text-xs mt-1 opacity-75">{format(day, 'MMM', { locale: dateLocale })}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slots */}
          <div>
            {(isLoadingSlots || slotsData.length > 0) && (
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                {t('bookings:reschedule.availableTimes', { date: format(selectedDate, dateFormats.full, { locale: dateLocale }) })}
              </h3>
            )}

            {isLoadingSlots ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-[52px] bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : slotsData.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">
                  {t('bookings:reschedule.noSlots', 'No available slots for this date')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {slotsData.map((slot) => {
                  const startTime = slot.startTime || slot.start_time;
                  const providerName =
                    slot.provider?.displayName ||
                    (slot.provider as any)?.name ||
                    'Provider';

                  if (!startTime) return null;

                  const slotTime = parseISO(startTime);
                  const isSelected = selectedSlot?.id === slot.id;

                  return (
                    <button
                      key={slot.id}
                      type="button"
                      onClick={() => setSelectedSlot(slot)}
                      className={cn(
                        'min-h-[52px] p-3 rounded-lg text-center',
                        isSelected
                          ? 'bg-blue-500 text-white shadow-md'
                          : 'bg-white border border-gray-200 hover:border-blue-300 active:bg-blue-50'
                      )}
                    >
                      <p className="font-medium text-sm md:text-base">
                        {format(slotTime, 'HH:mm')}
                      </p>
                      {slot.provider && (
                        <p className="text-xs mt-1 opacity-75 line-clamp-1">
                          {providerName}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Confirm button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:actions.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={() => rescheduleMutation.mutate()}
              disabled={!selectedSlot || rescheduleMutation.isPending}
            >
              {rescheduleMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('bookings:reschedule.rescheduling', 'Rescheduling...')}
                </>
              ) : (
                t('bookings:reschedule.confirm', 'Confirm Reschedule')
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
